import React, { useRef } from "react";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import styled, { useTheme } from "styled-components/macro";

import IconTextButton from "components/Button/IconTextButton";
import { MarkChips, LabelChip } from "components/Chip";
import { Row, Column } from "components/Layout";
import { LotLabelsModal } from "components/LotLabelsModal";
import { MarkingModal } from "components/MarkingModal";
import { Subtitle } from "components/Text";

import { userTypes } from "constants/users";

import { ForUserType } from "containers/ForUserType";

import { formatSaleLotOverflow } from "lib/saleLot/formatSaleLotOverflow";

import { getScanCountColor } from "components/ScanningScreen/Elements";

import { getSaleLotsBySale, getLabels } from "selectors";

import { useToggle } from "hooks";
import { useIsSmallMobile } from "hooks/useScreenSize";

const ScannedCountText = styled.span`
  ${({ countColor }) => countColor && `color: ${countColor};`}
`;

const HeaderSection = styled(Column)`
  flex-grow: 0;
  flex-shrink: 1;
  min-width: 0;
  max-width: 300px;
  margin: 0 ${({ theme }) => theme.space[1]}px;
`;

const getContainerFontSize = (hasItems, itemCount, theme, totalLength) => {
  if (!hasItems) {
    return theme.fontSizes.charlie;
  }
  if (itemCount > 3 || totalLength > 30) {
    return theme.fontSizes.alpha;
  }
  if (itemCount > 2 || totalLength > 20) {
    return theme.fontSizes.beta;
  }
  return theme.fontSizes.charlie;
};

function HeaderSectionComponent({
  title,
  children,
  onClickAdd,
  buttonRef,
  hasItems,
  itemCount,
  totalLength,
}) {
  const theme = useTheme();

  return (
    <HeaderSection>
      <Subtitle align="center">{title}</Subtitle>
      {onClickAdd ? (
        <Row alignCenter gridGap={1}>
          {hasItems && (
            <Row
              minHeight={`${theme.space[4]}px`}
              alignCenter
              justifyStart
              flexWrap
              fontSize={`${getContainerFontSize(
                hasItems,
                itemCount,
                theme,
                totalLength,
              )}px`}
              fontWeight="medium"
            >
              {children}
            </Row>
          )}
          <div ref={buttonRef}>
            <IconTextButton
              icon={faPlus}
              onClick={onClickAdd}
              inline
              iconSize={18}
              textSize={14}
            >
              Add
            </IconTextButton>
          </div>
        </Row>
      ) : (
        <Row
          minHeight={`${theme.space[4]}px`}
          alignCenter
          justifyCenter
          flexWrap
          fontWeight="medium"
        >
          {children}
        </Row>
      )}
    </HeaderSection>
  );
}

HeaderSectionComponent.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  onClickAdd: PropTypes.func,
  buttonRef: PropTypes.object,
  hasItems: PropTypes.bool,
  itemCount: PropTypes.number,
  totalLength: PropTypes.number,
};

function SaleLotDetailHeader(props) {
  const { saleLotId } = props;

  const [isMarkingModalVisible, toggleIsMarkingModalVisible] = useToggle(false);
  const [isLabelsModalVisible, toggleIsLabelsModalVisible] = useToggle(false);
  const markingButtonRef = useRef(null);
  const useSmallMobile = useIsSmallMobile();
  const theme = useTheme();

  const saleLot = useSelector(state =>
    getSaleLotsBySale(state).find(saleLot => saleLot.id === saleLotId),
  );
  const allLabels = useSelector(getLabels);

  if (!saleLotId || !saleLot) {
    return null;
  }
  const {
    marks,
    labels = [],
    penName,
    scannedCount,
    quantity,
    quantityProgeny,
    overflowPen,
    overflowQuantity,
  } = saleLot;

  const penText = `${penName} ${formatSaleLotOverflow(
    overflowPen,
    overflowQuantity,
  )}`;

  const totalQuantity = quantity + (quantityProgeny || 0);
  const scannedCountColor = getScanCountColor(
    theme,
    scannedCount,
    totalQuantity,
  );

  const hasMarks = marks.length > 0;
  const hasLabels = labels.length > 0;

  const marksLength = marks.reduce(
    (acc, mark) => acc + (mark.location?.length || 0),
    0,
  );
  const labelsLength = labels.reduce(
    (acc, labelId) => acc + (allLabels[labelId]?.name?.length || 0),
    0,
  );
  const labelsSection = (
    <HeaderSectionComponent
      title="Labels"
      onClickAdd={toggleIsLabelsModalVisible}
      hasItems={hasLabels}
      itemCount={labels.length}
      totalLength={labelsLength}
    >
      {labels.map(labelId => (
        <LabelChip key={labelId} labelId={labelId} />
      ))}
    </HeaderSectionComponent>
  );

  return (
    <ForUserType
      userType={[userTypes.LIVESTOCK_AGENT, userTypes.SALEYARD_ADMIN]}
    >
      <Row justifyAround padding={1} alignStart>
        <HeaderSectionComponent title="Pen">{penText}</HeaderSectionComponent>

        <HeaderSectionComponent title="Scanned">
          <ScannedCountText countColor={scannedCountColor}>
            {`${scannedCount} of ${totalQuantity}`}
          </ScannedCountText>
        </HeaderSectionComponent>

        <HeaderSectionComponent
          title="Marks"
          onClickAdd={toggleIsMarkingModalVisible}
          buttonRef={markingButtonRef}
          hasItems={hasMarks}
          itemCount={marks.length}
          totalLength={marksLength}
          fontSize="delta"
        >
          <MarkChips marks={marks} />
        </HeaderSectionComponent>
        {!useSmallMobile && labelsSection}
      </Row>

      {useSmallMobile && (
        <Row justifyAround padding={1} alignStart>
          {labelsSection}
        </Row>
      )}

      {isMarkingModalVisible && (
        <MarkingModal
          onClose={toggleIsMarkingModalVisible}
          saleLot={saleLot}
          anchorEl={markingButtonRef}
        />
      )}
      {isLabelsModalVisible && (
        <LotLabelsModal
          onClose={toggleIsLabelsModalVisible}
          saleLot={saleLot}
        />
      )}
    </ForUserType>
  );
}

export default React.memo(SaleLotDetailHeader);
